import { Controller } from "@hotwired/stimulus"

// Listens for `toast:show` custom events on document.
// Dispatches: document.dispatchEvent(new CustomEvent("toast:show", { detail: { message, type } }))
//
// Types: success (3s), info (5s), warning (8s), error (persistent)
// Max 3 visible — auto-dismisses oldest on overflow.
// Respects prefers-reduced-motion.

export default class extends Controller {
  static targets = ["container"]
  static values = { maxVisible: { type: Number, default: 3 } }

  connect() {
    this.toasts = []
    this._handler = this.showToast.bind(this)
    document.addEventListener("toast:show", this._handler)
  }

  disconnect() {
    document.removeEventListener("toast:show", this._handler)
    this.toasts.forEach(t => clearTimeout(t.timer))
    this.toasts = []
  }

  showToast(event) {
    const { message, type = "info" } = event.detail || {}
    if (!message) return

    const toast = this._createToastElement(message, type)
    this.containerTarget.appendChild(toast)

    const entry = { element: toast, timer: null }

    // Auto-dismiss (error is persistent)
    const duration = this._duration(type)
    if (duration > 0) {
      entry.timer = setTimeout(() => this._dismiss(entry), duration)
    }

    this.toasts.push(entry)

    // Enforce max visible
    while (this.toasts.length > this.maxVisibleValue) {
      this._dismiss(this.toasts[0])
    }

    // Animate in
    requestAnimationFrame(() => toast.classList.add("toast-visible"))
  }

  dismiss(event) {
    const el = event.currentTarget.closest("[data-toast-item]")
    const entry = this.toasts.find(t => t.element === el)
    if (entry) this._dismiss(entry)
  }

  _dismiss(entry) {
    if (!entry || !entry.element.parentNode) return
    clearTimeout(entry.timer)
    this.toasts = this.toasts.filter(t => t !== entry)

    if (this._prefersReducedMotion()) {
      entry.element.remove()
    } else {
      entry.element.classList.remove("toast-visible")
      entry.element.addEventListener("transitionend", () => entry.element.remove(), { once: true })
      // Fallback removal in case transitionend doesn't fire
      setTimeout(() => entry.element.remove(), 350)
    }
  }

  _createToastElement(message, type) {
    const div = document.createElement("div")
    div.setAttribute("data-toast-item", "")
    div.setAttribute("role", type === "error" ? "alert" : "status")
    div.className = `alert ${this._alertClass(type)} shadow-lg transition-all duration-300 opacity-0 translate-y-2`

    const span = document.createElement("span")
    span.textContent = message
    div.appendChild(span)

    const btn = document.createElement("button")
    btn.type = "button"
    btn.className = "btn btn-ghost btn-xs"
    btn.setAttribute("data-action", "toast#dismiss")
    btn.setAttribute("aria-label", "Dismiss")
    btn.textContent = "\u00D7"
    div.appendChild(btn)

    return div
  }

  _alertClass(type) {
    const map = { success: "alert-success", info: "alert-info", warning: "alert-warning", error: "alert-error" }
    return map[type] || "alert-info"
  }

  _duration(type) {
    const map = { success: 3000, info: 5000, warning: 8000, error: 0 }
    return map[type] ?? 5000
  }

  _prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }
}
